import { useTranslations } from '@lang';
import rewriteHttp from '@utils/rewriteHttp';
import { useMemo } from 'react';
import { PackageExtrasTemplate } from 'voyager';
import type { PackageExtrasTemplateLabels, PackageExtrasTemplateProps } from 'voyager';
import { useCurrentStep, usePackageConfiguration, useWizardNavigation } from '../../contexts';
import { useNextStepLabel } from '../hooks/useNextStepLabel';
import { usePackageExtrasGuests } from '../hooks/usePackageExtrasGuests';
import { usePackageStepProgress } from '../hooks/usePackageStepProgress';

type PackageExtrasPageProps = Pick<
  PackageExtrasTemplateProps,
  'items' | 'onAdd' | 'onRemove' | 'onConfirmGuestSelection' | 'onConfirmSelection'
> & {
  labels: Omit<PackageExtrasTemplateLabels, 'footerTitle' | 'ctaLabel' | 'progressValueText'>;
};

export const PackageExtrasPage = ({ labels, ...handlers }: PackageExtrasPageProps) => {
  const { rate, room, language } = usePackageConfiguration();
  const { handleNextStep, handlePreviousStep } = useWizardNavigation();
  const { packageTotalPrice, isSubmitting } = useCurrentStep();
  const guests = usePackageExtrasGuests();
  const { currentStep, totalSteps } = usePackageStepProgress();
  const ctaLabel = useNextStepLabel();
  const { t } = useTranslations();

  const templateLabels = useMemo(
    () => ({
      ...labels,
      footerTitle: rate.title ?? labels.title,
      ctaLabel,
      progressValueText: t('aria.bookingStep', { current: currentStep, total: totalSteps }),
    }),
    [labels, rate.title, ctaLabel, t, currentStep, totalSteps]
  );

  const selectedRoom = rate.rooms?.find(({ id }) => String(id) === String(room.id));
  const imageSource = rate.imageUrl || rate.images?.[0] || selectedRoom?.imageUrl || undefined;

  return (
    <PackageExtrasTemplate
      {...handlers}
      imageUrl={imageSource ? rewriteHttp(imageSource) : undefined}
      imageAlt={rate.title}
      guests={guests.length > 1 ? guests : undefined}
      currentStep={currentStep}
      totalSteps={totalSteps}
      totalAmount={packageTotalPrice.amount}
      currencyCode={packageTotalPrice.currencyCode}
      locale={language}
      onBack={handlePreviousStep}
      onCtaClick={handleNextStep}
      ctaDisabled={isSubmitting}
      isLoading={isSubmitting}
      labels={templateLabels}
    />
  );
};
